import React, { useMemo, useCallback } from 'react';
import type { ChartOptions } from 'chart.js';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import type { Player } from '@/types';
import { getTeamColors } from '../../utils/colorUtil';
import { getTeamDisplayNameSafe } from '@/utils/team';

const chartConfig = {
  colors: {
    tooltip: { bg: 'rgba(17, 24, 39, 0.9)', text: '#ffffff' },
    grid: '#E5E7EB',
    ticks: '#6B7280',
    text: '#111827',
  },
  bar: {
    opacity: 0.8,
    borderWidth: 0,
    borderRadius: 2,
    percentage: 1.2,
  },
  fonts: {
    axis: {
      size: 14,
      weight: 400,
      family: 'Dengxian, Microsoft YaHei, sans-serif',
    },
    playerInfo: {
      size: 14,
      weight: 400,
      family: 'Dengxian, Microsoft YaHei, sans-serif',
    },
  },
  layout: { paddingLeft: 10, paddingRight: 50 },
  tooltip: {
    cornerRadius: 8,
  },
};

// 注册 Chart.js 组件
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface DynamicRankingProps {
  data: Player[];
  title?: string;
  topCount?: number;
  currentDate?: string;
  height?: number;
  className?: string;
  minELO?: number;
}

const DynamicRanking: React.FC<DynamicRankingProps> = ({
  data,
  topCount = 10,
  currentDate = new Date().toLocaleDateString('zh-CN'),
  height = 400,
  className = '',
  minELO = 2000,
}) => {
  const rankingData = useMemo(() => {
    return [...data]
      .sort((a, b) => b.currentElo - a.currentElo)
      .slice(0, topCount);
  }, [data, topCount]);

  const teamColorMap = useMemo(() => {
    const teams = Array.from(
      new Set(
        rankingData.map(player => getTeamDisplayNameSafe(player.currentTeam))
      )
    );
    const colors = getTeamColors(teams.length);
    const map = new Map<string, string>();

    teams.forEach((team, index) => {
      map.set(team, colors[index]);
    });

    return map;
  }, [rankingData]);

  const getTeamColor = useCallback(
    (teamName: string) => {
      return teamColorMap.get(teamName) || '#b3b3b3';
    },
    [teamColorMap]
  );

  const chartData = useMemo(() => {
    const labels = rankingData.map((_, index) => `#${index + 1}`);
    const eloScores = rankingData.map(player => player.currentElo);

    const backgroundColors = rankingData.map(player => {
      const teamName = getTeamDisplayNameSafe(player.currentTeam);
      const color = getTeamColor(teamName);
      const rgb = color.replace('#', '');
      const r = parseInt(rgb.substring(0, 2), 16);
      const g = parseInt(rgb.substring(2, 4), 16);
      const b = parseInt(rgb.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${chartConfig.bar.opacity})`;
    });

    const borderColors = rankingData.map(player => {
      const teamName = getTeamDisplayNameSafe(player.currentTeam);
      return getTeamColor(teamName);
    });

    return {
      labels,
      datasets: [
        {
          label: 'ELO 等级分',
          data: eloScores,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: chartConfig.bar.borderWidth,
          borderRadius: chartConfig.bar.borderRadius,
          borderSkipped: false,
          barPercentage: chartConfig.bar.percentage,
        },
      ],
    };
  }, [rankingData, getTeamColor]);

  // 图表配置选项
  const chartOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      indexAxis: 'y' as const, // 横向柱状图
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false, // 隐藏图例
        },
        tooltip: {
          backgroundColor: chartConfig.colors.tooltip.bg,
          titleColor: chartConfig.colors.tooltip.text,
          bodyColor: chartConfig.colors.tooltip.text,
          cornerRadius: chartConfig.tooltip.cornerRadius,
          titleFont: {
            size: 14,
            weight: 600,
          },
          bodyFont: {
            size: 13,
          },
          callbacks: {
            title: context => {
              const index = context[0].dataIndex;
              const player = rankingData[index];
              return `#${index + 1} ${player.playerId}`;
            },
            label: context => {
              const value = context.parsed.x;
              const index = context.dataIndex;
              const player = rankingData[index];
              return [
                `ELO: ${value.toLocaleString()}`,
                `战队: ${getTeamDisplayNameSafe(player.currentTeam)}`,
                `真名: ${player.realName || '未知'}`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: false,
          min: minELO,
          position: 'top' as const,
          grid: {
            color: chartConfig.colors.grid,
            drawBorder: false,
          },
          ticks: {
            color: chartConfig.colors.ticks,
            font: chartConfig.fonts.axis,
            callback: value => {
              return (value as number).toLocaleString();
            },
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            color: chartConfig.colors.ticks,
            font: chartConfig.fonts.axis,
          },
        },
      },
      layout: {
        padding: {
          left: chartConfig.layout.paddingLeft,
          right: chartConfig.layout.paddingRight, // 为选手名称留出空间
        },
      },
      onHover: (event, activeElements) => {
        const target = event.native?.target as HTMLElement;
        if (target) {
          target.style.cursor =
            activeElements.length > 0 ? 'pointer' : 'default';
        }
      },
    }),
    [rankingData, minELO]
  );

  const chartPlugins = useMemo(
    () => [
      {
        id: 'playerLabels',
        afterDatasetsDraw: (chart: unknown) => {
          const chartInstance = chart as {
            ctx: CanvasRenderingContext2D;
            data: { datasets: Array<{ data: number[] }> };
            getDatasetMeta: (index: number) => {
              data: Array<{ x: number; y: number }>;
            };
          };
          const ctx = chartInstance.ctx;

          chartInstance.data.datasets[0].data.forEach(
            (value: number, index: number) => {
              const meta = chartInstance.getDatasetMeta(0);
              const bar = meta.data[index];
              const player = rankingData[index];

              if (bar) {
                const teamName = getTeamDisplayNameSafe(player.currentTeam);
                const playerInfo = `${teamName}.${player.playerId} (${player.realName || '未知'})`;

                ctx.fillStyle = chartConfig.colors.text;
                ctx.font = `${chartConfig.fonts.playerInfo.weight} ${chartConfig.fonts.playerInfo.size}px ${chartConfig.fonts.playerInfo.family}`;
                ctx.textAlign = 'right';
                ctx.textBaseline = 'middle';

                const playerInfoX = bar.x - 10;
                const playerInfoY = bar.y;
                ctx.fillText(playerInfo, playerInfoX, playerInfoY);

                ctx.fillStyle = chartConfig.colors.text;
                ctx.font = `${chartConfig.fonts.playerInfo.weight} ${chartConfig.fonts.playerInfo.size}px ${chartConfig.fonts.playerInfo.family}`;
                ctx.textAlign = 'left';

                const scoreX = bar.x + 10;
                const scoreY = bar.y;
                ctx.fillText(value.toLocaleString(), scoreX, scoreY);
              }
            }
          );
        },
      },
    ],
    [rankingData]
  );

  if (!data || data.length === 0) {
    return (
      <div
        className={`flex items-center justify-center bg-white border border-border rounded-xl ${className}`}
        style={{ height }}
      >
        <p className='text-text-secondary'>暂无排名数据</p>
      </div>
    );
  }

  return (
    <div
      className={`bg-white border border-border rounded-xl p-6 shadow-card ${className}`}
    >
      {/* 日期显示 */}
      <div className='flex justify-between items-center mb-4'>
        <h3 className='text-lg font-semibold text-text-primary'>
          Top {topCount} 排名
        </h3>
        <div className='text-sm text-text-secondary bg-gray-100 px-3 py-1 rounded'>
          {currentDate}
        </div>
      </div>

      {/* 图表容器 */}
      <div style={{ height: Math.max(100, rankingData.length * 35) }}>
        <Bar data={chartData} options={chartOptions} plugins={chartPlugins} />
      </div>
    </div>
  );
};

export default DynamicRanking;
